/* ============================================================
   Dathent — socialCapacity.js (TASK-297, EPIC-029 #409)
   Pure helper for the connect-surface UX: a user whose plan's
   social_accounts_total cap is already reached must see it BEFORE trying to
   connect, never after a redirect that dead-ends on auth-worker's
   QUOTA_EXCEEDED (social-account-capacity.ts assertSocialAccountCapacity).

   Prefers `capacity` — the { limit, used, remaining } object auth-worker's
   GET /social/accounts now returns (getSocialAccountCapacity, TASK-297): a
   TRUE, non-brand-scoped live count computed by the exact same query/gate
   the OAuth callback enforces.

   Falls back to entitlements.limits.social_accounts_total + accounts.length
   only when no capacity object is available yet — an approximation that can
   UNDERCOUNT for a multibrand user with accounts split across brands
   (GET /social/accounts scopes `accounts` to the active brand once one is
   set), and that can't be repaired from GET /api/billing/entitlements
   either: its usage.social_accounts_total is always `{ used: 0 }` because
   nothing ever writes usage_counters for this STOCK metric — every call
   site gates it via checkTotalLimit's live COUNT, never
   reserveQuota/incrementUsage. So treat the fallback as best-effort only,
   never authoritative for the connect/no-connect decision.
   ============================================================ */

/**
 * @param {object} opts
 * @param {{limit: number|null, used: number, remaining: number|null}|null} [opts.capacity]
 *   From GET /social/accounts (preferred, accurate).
 * @param {object|null} [opts.entitlements]  GET /api/billing/entitlements payload.
 * @param {Array} [opts.accounts]  The (possibly brand-scoped) connected-accounts list.
 * @returns {{limit: number|null, used: number, remaining: number|null, reached: boolean}}
 */
export function socialCapacityFrom({ capacity, entitlements, accounts } = {}) {
  if (capacity && typeof capacity === 'object') {
    const limit = capacity.limit === undefined ? null : capacity.limit;
    const used = Number(capacity.used) || 0;
    const remaining = capacity.remaining === undefined ? null : capacity.remaining;
    return { limit, used, remaining, reached: remaining === 0 };
  }
  const rawLimit = entitlements?.limits?.social_accounts_total;
  const limit = rawLimit === undefined ? null : rawLimit;
  const used = Array.isArray(accounts) ? accounts.length : 0;
  const remaining = limit === null ? null : Math.max(0, limit - used);
  return { limit, used, remaining, reached: remaining === 0 };
}
